import { meterService } from "../services/meterService";
import { appStore } from "../state/store";
import { formatNaira } from "./format";
import { byId } from "./dom";
import type { UsageEntry } from "../types";

let loadedForMeterId: string | null = null;

export function initUsageView(): void {
  appStore.subscribe((state) => {
    const meter = state.meters[0];
    if (meter && meter.id !== loadedForMeterId) {
      loadedForMeterId = meter.id;
      void loadAndRender(meter.id);
    }
    if (!meter) {
      renderEmpty();
    }
  });
}

function renderEmpty(): void {
  const chartBarsEl = byId("chart-bars");
  const usageLogEl = byId("usage-log");
  chartBarsEl.innerHTML = "";
  usageLogEl.innerHTML = "";
  const empty = document.createElement("p");
  empty.className = "panel-subheading";
  empty.textContent = "Link a meter to see usage history.";
  usageLogEl.appendChild(empty);
}

async function loadAndRender(meterId: string): Promise<void> {
  const entries = await meterService.getUsageHistory(meterId, 7);
  renderChart(entries);
  renderUsageLog(entries);
}

function renderChart(entries: UsageEntry[]): void {
  const chartBarsEl = byId("chart-bars");
  const chartTooltipEl = byId("chart-tooltip");
  chartBarsEl.innerHTML = "";

  if (entries.length === 0) return;
  const maxKwh = Math.max(...entries.map((e) => e.kwh));

  function showTooltip(entry: UsageEntry, col: HTMLElement): void {
    chartBarsEl.querySelectorAll(".chart-bar-col").forEach((c) => c.classList.remove("is-selected"));
    col.classList.add("is-selected");
    const dayLabel = new Date(entry.date).toLocaleDateString("en-NG", { weekday: "short" });
    chartTooltipEl.textContent = `${dayLabel} \u00b7 ${entry.kwh.toFixed(1)} kWh`;
    chartTooltipEl.hidden = false;
  }

  entries.forEach((entry, index) => {
    const col = document.createElement("div");
    col.className = "chart-bar-col";
    col.dataset.index = String(index);

    const bar = document.createElement("div");
    bar.className = "chart-bar";
    const heightPct = Math.max(6, Math.round((entry.kwh / maxKwh) * 100));
    bar.style.height = heightPct + "%";

    const label = document.createElement("span");
    label.className = "chart-bar-label";
    label.textContent = new Date(entry.date).toLocaleDateString("en-NG", { weekday: "short" });

    col.appendChild(bar);
    col.appendChild(label);
    col.addEventListener("click", () => showTooltip(entry, col));
    chartBarsEl.appendChild(col);
  });

  const lastEntry = entries[entries.length - 1];
  const lastCol = chartBarsEl.lastElementChild as HTMLElement | null;
  if (lastEntry && lastCol) showTooltip(lastEntry, lastCol);
}

function renderUsageLog(entries: UsageEntry[]): void {
  const usageLogEl = byId("usage-log");
  usageLogEl.innerHTML = "";

  [...entries].reverse().forEach((entry) => {
    const row = document.createElement("li");
    row.className = "usage-log-row";
    const dayLabel = new Date(entry.date).toLocaleDateString("en-NG", { weekday: "short" });
    const sourceLabel = entry.sourceType === "grid" ? "Grid" : "Generator";
    row.innerHTML = `
      <div>
        <div class="usage-log-date">${dayLabel}</div>
        <div class="usage-log-source">${sourceLabel}</div>
      </div>
      <div class="usage-log-figures">
        <div class="usage-log-kwh">${entry.kwh.toFixed(1)} kWh</div>
        <div class="usage-log-cost">\u20a6${formatNaira(entry.costKobo)}</div>
      </div>
    `;
    usageLogEl.appendChild(row);
  });
}
